import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Wallet, ArrowRightLeft, TrendingUp, Coins, RefreshCw, ChevronDown, ChevronUp } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function CashBoxReport() {
  const { currentCompany } = useAuth();
  const [report, setReport] = useState(null);
  const [boxes, setBoxes] = useState([]);
  const [rates, setRates] = useState({});
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState({ from_box_id: "", to_box_id: "", amount: 0, to_amount: "", exchange_rate: "", notes: "" });
  const [expanded, setExpanded] = useState({});
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, b, cur] = await Promise.all([
        api.get("/cashboxes/report/balances"),
        api.get("/cashboxes"),
        api.get("/currencies").catch(() => ({ data: { rates: {} } })),
      ]);
      setReport(r.data); setBoxes(b.data);
      setRates((cur.data && cur.data.rates) || {});
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [currentCompany?.id]);

  const refreshRates = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/currencies/fetch");
      toast.success(`تم تحديث ${Object.keys(data.rates || {}).length} عملة`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setRefreshing(false); }
  };

  const openTransfer = () => { setForm({ from_box_id: "", to_box_id: "", amount: 0, to_amount: "", exchange_rate: "", notes: "" }); setDlg(true); };
  const doTransfer = async () => {
    if (!form.from_box_id || !form.to_box_id) return toast.error("اختر الصندوقين");
    if (form.from_box_id === form.to_box_id) return toast.error("لا يمكن التحويل لنفس الصندوق");
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) return toast.error("المبلغ غير صحيح");
    try {
      const payload = { from_box_id: form.from_box_id, to_box_id: form.to_box_id, amount: amt, notes: form.notes };
      if (form.to_amount && parseFloat(form.to_amount) > 0) payload.to_amount = parseFloat(form.to_amount);
      else if (form.exchange_rate && parseFloat(form.exchange_rate) > 0) payload.exchange_rate = parseFloat(form.exchange_rate);
      const { data } = await api.post("/cashboxes/transfer", payload);
      toast.success(`تم التحويل: ${data.from_amount} ${data.from_currency} → ${data.to_amount} ${data.to_currency}`);
      setDlg(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const fromBox = boxes.find(b => b.id === form.from_box_id);
  const toBox = boxes.find(b => b.id === form.to_box_id);
  const differentCurrency = fromBox && toBox && fromBox.currency !== toBox.currency;
  const suggestedRate = (() => {
    if (!differentCurrency) return null;
    const from = rates[fromBox.currency]; const to = rates[toBox.currency];
    if (from > 0 && to > 0) return from / to;
    return null;
  })();

  const fmt = (n) => (Math.round(n * 100) / 100).toLocaleString("ar-EG");

  return (
    <div className="space-y-4" data-testid="cashbox-report">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading font-bold text-2xl flex items-center gap-2"><Wallet className="w-6 h-6 text-primary" /> أرصدة الصناديق</h1>
          <p className="text-sm text-muted-foreground mt-1">إجمالي كل العملات محسوباً بالعملة الأساسية</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={refreshRates} variant="outline" disabled={refreshing} data-testid="refresh-rates-btn"><RefreshCw className={`w-4 h-4 me-1 ${refreshing ? "animate-spin" : ""}`} /> تحديث الأسعار</Button>
          <Button onClick={openTransfer} className="gap-2" data-testid="new-transfer-btn"><ArrowRightLeft className="w-4 h-4" /> تحويل بين صناديق</Button>
        </div>
      </div>

      {loading ? <div className="text-center py-8 text-muted-foreground">جاري التحميل...</div> : report && (
        <>
          {/* Grand Total */}
          <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-primary/10">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-primary text-primary-foreground grid place-items-center shrink-0"><TrendingUp className="w-7 h-7" /></div>
              <div>
                <div className="text-xs text-muted-foreground mb-1">إجمالي كل الصناديق بالعملة الأساسية</div>
                <div className="text-3xl font-bold font-mono text-primary" data-testid="grand-total">{fmt(report.total_in_base)} <span className="text-lg">{report.base_currency}</span></div>
                <div className="text-[10px] text-muted-foreground mt-1">آخر تحديث: {new Date(report.as_of).toLocaleString("ar-EG")}</div>
              </div>
            </CardContent>
          </Card>

          {/* By Currency */}
          <div className="grid gap-2">
            {report.by_currency.map(g => (
              <Card key={g.currency} data-testid={`cur-${g.currency}`}>
                <CardHeader className="pb-2 cursor-pointer" onClick={() => setExpanded({ ...expanded, [g.currency]: !expanded[g.currency] })}>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <span className="w-9 h-9 rounded-full bg-primary/10 text-primary grid place-items-center text-xs font-mono font-bold">{g.currency}</span>
                      {g.boxes.length} صندوق
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <div className="text-2xl font-bold font-mono">{fmt(g.total)}</div>
                      {expanded[g.currency] ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                    </div>
                  </div>
                </CardHeader>
                {expanded[g.currency] && (
                  <CardContent className="pt-0 space-y-1">
                    {g.boxes.map(b => (
                      <div key={b.id} className="flex justify-between text-sm border-t pt-1.5" data-testid={`box-${b.id}`}>
                        <span>{b.name}</span>
                        <span className={`font-mono ${b.balance < 0 ? "text-red-600" : ""}`}>{fmt(b.balance)}</span>
                      </div>
                    ))}
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Transfer Dialog */}
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="max-w-lg" data-testid="transfer-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ArrowRightLeft className="w-5 h-5 text-primary" /> تحويل بين صناديق</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>من صندوق</Label>
              <select className="w-full h-10 px-2 border rounded bg-background text-sm" value={form.from_box_id} onChange={e => setForm({ ...form, from_box_id: e.target.value })} data-testid="from-box">
                <option value="">— اختر —</option>
                {boxes.map(b => <option key={b.id} value={b.id}>{b.name || `صندوق ${b.currency}`} — {b.currency} · {fmt(b.balance || 0)}</option>)}
              </select>
            </div>
            <div>
              <Label>إلى صندوق</Label>
              <select className="w-full h-10 px-2 border rounded bg-background text-sm" value={form.to_box_id} onChange={e => setForm({ ...form, to_box_id: e.target.value })} data-testid="to-box">
                <option value="">— اختر —</option>
                {boxes.filter(b => b.id !== form.from_box_id).map(b => <option key={b.id} value={b.id}>{b.name || `صندوق ${b.currency}`} — {b.currency} · {fmt(b.balance || 0)}</option>)}
              </select>
            </div>
            <div>
              <Label>المبلغ ({fromBox?.currency || ""})</Label>
              <Input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} className="text-lg font-mono" data-testid="amount-input" />
            </div>
            {differentCurrency && (
              <div className="p-3 rounded border-2 border-amber-300 bg-amber-50 space-y-2" data-testid="fx-block">
                <div className="text-xs font-bold text-amber-800 flex items-center gap-1"><Coins className="w-3.5 h-3.5" /> تحويل بين عملتين مختلفتين</div>
                {suggestedRate && <div className="text-[11px] text-amber-700">السعر التلقائي: 1 {fromBox.currency} = <b>{suggestedRate.toFixed(4)}</b> {toBox.currency}</div>}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">سعر الصرف يدوياً (اختياري)</Label>
                    <Input type="number" value={form.exchange_rate} onChange={e => setForm({ ...form, exchange_rate: e.target.value, to_amount: "" })} placeholder={suggestedRate ? suggestedRate.toFixed(4) : "أدخل"} className="text-sm" data-testid="rate-input" />
                  </div>
                  <div>
                    <Label className="text-xs">أو أدخل المبلغ الوصولي</Label>
                    <Input type="number" value={form.to_amount} onChange={e => setForm({ ...form, to_amount: e.target.value, exchange_rate: "" })} placeholder={suggestedRate && form.amount ? (parseFloat(form.amount) * suggestedRate).toFixed(2) : "أدخل"} className="text-sm" data-testid="to-amount-input" />
                  </div>
                </div>
                <div className="text-[10px] text-muted-foreground">إذا لم تُدخل شيئاً، سيُحسب المبلغ من الأسعار المخزّنة تلقائياً</div>
              </div>
            )}
            <div><Label>ملاحظات</Label><Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="سبب التحويل..." /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDlg(false)}>إلغاء</Button><Button onClick={doTransfer} data-testid="confirm-transfer-btn"><ArrowRightLeft className="w-4 h-4 me-1" /> تحويل</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
